/* ============================================================
   PARTNER STUDIO — pintu PIN (PLAN_PARTNER.md, skrin 1 lakaran)
   ============================================================
   PIN Partner = PIN PER-AKAUN, berasingan daripada PIN admin.
   Kali pertama: Partner cipta PIN (taip 2×). Seterusnya: masukkan PIN.
   Semua semakan berlaku di SERVER (partnerStudio → pinCipta/pinSahkan);
   page ini hanya menghantar 6 digit dan menerima token sesi.
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { Btn } from '../design/ui';
import { panggilPartner, simpanToken, kodRalat } from '../services/partnerStudioService';

const KOSONG = ['', '', '', '', '', ''];

export default function PartnerPinPintu({ pinAda, kunciSehingga: kunciAwal, onMasuk }) {
    const [digit, setDigit] = useState([...KOSONG]);
    const [peringkat, setPeringkat] = useState(pinAda ? 'masuk' : 'cipta'); // masuk | cipta | sahkan
    const [ralat, setRalat] = useState('');
    const [sibuk, setSibuk] = useState(false);
    const [kunciSehingga, setKunciSehingga] = useState(kunciAwal || null);
    const [kini, setKini] = useState(Date.now());
    const pertama = useRef('');
    const kotak = useRef([]);

    useEffect(() => { kotak.current[0]?.focus(); }, [peringkat]);
    useEffect(() => {
        if (!kunciSehingga) return undefined;
        const t = setInterval(() => {
            setKini(Date.now());
            if (Date.now() >= kunciSehingga) { setKunciSehingga(null); setRalat(''); }
        }, 1000);
        return () => clearInterval(t);
    }, [kunciSehingga]);

    const dikunci = !!kunciSehingga && kini < kunciSehingga;
    const bakiSaat = dikunci ? Math.ceil((kunciSehingga - kini) / 1000) : 0;

    const hantar = async (pin) => {
        setRalat('');
        if (peringkat === 'cipta') {
            pertama.current = pin;
            setDigit([...KOSONG]);
            setPeringkat('sahkan');
            return;
        }
        if (peringkat === 'sahkan' && pin !== pertama.current) {
            setRalat("PINs don't match. Create your PIN again.");
            pertama.current = '';
            setDigit([...KOSONG]);
            setPeringkat('cipta');
            return;
        }
        setSibuk(true);
        try {
            const hasil = await panggilPartner(peringkat === 'masuk' ? 'pinSahkan' : 'pinCipta', { pin });
            simpanToken(hasil.token);
            onMasuk();
        } catch (e) {
            const kod = kodRalat(e);
            if (kod === 'resource-exhausted' && e?.details?.kunciSehingga) setKunciSehingga(e.details.kunciSehingga);
            if (kod === 'failed-precondition' && e?.details?.pin === 'tiada') setPeringkat('cipta');
            setRalat(e?.message || 'Could not check your PIN. Try again.');
            setDigit([...KOSONG]);
            setTimeout(() => kotak.current[0]?.focus(), 0);
        } finally {
            setSibuk(false);
        }
    };

    const ubah = (i, nilai) => {
        const v = nilai.replace(/\D/g, '');
        if (!v) { const d = [...digit]; d[i] = ''; setDigit(d); return; }
        // Tampal 6 digit sekaligus
        const d = [...digit];
        for (let k = 0; k < v.length && i + k < 6; k++) d[i + k] = v[k];
        setDigit(d);
        const seterus = Math.min(5, i + v.length);
        kotak.current[seterus]?.focus();
        if (d.every((x) => x)) hantar(d.join(''));
    };
    const kekunci = (i, e) => {
        if (e.key === 'Backspace' && !digit[i] && i > 0) kotak.current[i - 1]?.focus();
    };

    const tajuk = peringkat === 'masuk' ? 'Enter your 6-digit partner PIN'
        : peringkat === 'cipta' ? 'Create a 6-digit partner PIN'
            : 'Type the same PIN again';

    return (
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="s-card" style={{ width: '100%', maxWidth: 380, padding: '28px 24px', textAlign: 'center' }}>
                <Lock size={22} style={{ color: 'var(--s-ink-3)' }} aria-hidden="true" />
                <h1 className="s-title" style={{ fontSize: 20, margin: '10px 0 4px' }}>Partner Studio</h1>
                <p className="s-subtitle" style={{ marginBottom: 18 }}>{tajuk}</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    {digit.map((d, i) => (
                        <input
                            key={i}
                            ref={(el) => { kotak.current[i] = el; }}
                            type="password"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={6}
                            value={d}
                            disabled={sibuk || dikunci}
                            onChange={(e) => ubah(i, e.target.value)}
                            onKeyDown={(e) => kekunci(i, e)}
                            aria-label={`PIN digit ${i + 1}`}
                            className="s-input"
                            style={{ width: 42, height: 48, padding: 0, textAlign: 'center', fontSize: 18 }}
                        />
                    ))}
                </div>
                {sibuk && <p className="s-hint" style={{ marginTop: 12 }}><Loader2 size={13} className="animate-spin" style={{ verticalAlign: -2 }} /> Checking…</p>}
                {dikunci && <p className="s-hint" style={{ marginTop: 12, color: 'var(--s-stop)' }}>Locked. Try again in {Math.floor(bakiSaat / 60)}:{String(bakiSaat % 60).padStart(2, '0')}.</p>}
                {ralat && !dikunci && <p className="s-hint" style={{ marginTop: 12, color: 'var(--s-stop)' }}>{ralat}</p>}
                {peringkat === 'sahkan' && (
                    <div style={{ marginTop: 14 }}>
                        <Btn variant="quiet" size="sm" onClick={() => { pertama.current = ''; setDigit([...KOSONG]); setPeringkat('cipta'); }}>Start over</Btn>
                    </div>
                )}
                <p className="s-hint" style={{ marginTop: 16 }}>
                    {peringkat === 'masuk'
                        ? 'Locked for 5 minutes after 5 wrong tries. Forgot your PIN? Ask SESAAT to reset it.'
                        : "Don't use your birthday or 123456. You'll need this PIN every time you open Partner Studio."}
                </p>
            </div>
        </div>
    );
}
